/**
 * The non-provider bearer token an EHR launch or a Medical Director restore
 * hands us, and where it came from.
 *
 * These tokens are not provider-issued — there is no `/api/{provider}-session`
 * refresh path for them — so they bypass the auth cache entirely, and the axios
 * request interceptor branches on this token's PRESENCE to use it directly.
 * That branch runs synchronously outside React, which is why the getters below
 * exist alongside the atoms.
 *
 * One atom per auth field rather than one for the group. The token has five
 * readers and `provider` has three; a single atom would re-render token readers
 * every time the Medical Director restore hook writes a provider it already
 * had. `externalLaunchAuthAtom` composes the auth fields and launch context for
 * readers that want the whole shape, and is derived — so it cannot become a
 * write path back to a blob.
 *
 * Auth fields are deliberately NOT persisted: FHIR sessions are session-only
 * (the user re-launches from the EHR), and Medical Director has its own
 * persistence layer. Launch context is persisted in sessionStorage separately
 * so it survives route changes within the tab, matching the previous generic
 * EHR launch context behavior.
 */
import { type AuthProvider } from '@heidi/auth/contracts';
import { createSafeJotaiSessionStorage } from '@heidi/utils/jotai-storage';
import { atom, getDefaultStore } from 'jotai';
import { atomWithStorage } from 'jotai/utils';
import {
  ExternalLaunchType,
  type ExternalLaunchContext,
  type GenericSmartOnFhirLaunchContext,
} from '../../contracts/externalLaunchAuth.types';

export {
  ExternalLaunchType,
  type ExternalLaunchContext,
  type ExternalLaunchContextBase,
  type GenericSmartOnFhirLaunchContext,
  type HealthdirectLaunchContext,
  type MedtechLaunchContext,
  type OpenApiLaunchContext,
  type SgimedLaunchContext,
} from '../../contracts/externalLaunchAuth.types';
export const DEFAULT_GENERIC_EHR_LAUNCH_CONTEXT: GenericSmartOnFhirLaunchContext = {
  launchType: ExternalLaunchType.GENERIC_SMART_ON_FHIR,
  sessionId: null,
  ehrProvider: null,
  onboardingSessionId: null,
  showPushButton: true,
};

export const externalLaunchContextAtom = atomWithStorage<ExternalLaunchContext | null>(
  'externalLaunch:launchContext',
  null,
  createSafeJotaiSessionStorage(),
  { getOnInit: true }
);
export const externalLaunchTokenAtom = atomWithStorage<string | null>(
  'externalLaunch:token',
  null,
  createSafeJotaiSessionStorage(),
  { getOnInit: true }
);
export const externalLaunchProviderAtom = atomWithStorage<AuthProvider | null>(
  'externalLaunch:provider',
  null,
  createSafeJotaiSessionStorage(),
  { getOnInit: true }
);

export interface ExternalLaunchAuthState {
  token: string | null;
  /**
   * The auth provider the user is logged in with. For FHIR users this is
   * `AuthProvider.FHIR`; for Medical Director users it is whatever they
   * originally logged in with (`KINDE` / `AUTH0` / `KEYCLOAK` / …) — MD is a
   * persistence sidecar, NOT a distinct provider, and there is no
   * `AuthProvider.MEDICAL_DIRECTOR` enum value.
   */
  provider: AuthProvider | null;
  launchContext: ExternalLaunchContext | null;
}

export interface SetExternalLaunchDetailsPayload {
  token: string;
  provider: AuthProvider;
  launchContext: ExternalLaunchContext | null;
}

export interface SetExternalLaunchTokenPayload {
  token: string;
  provider: AuthProvider;
}

/** The whole shape, for readers that want more than one field. */
export const externalLaunchAuthAtom = atom<ExternalLaunchAuthState>((get) => ({
  token: get(externalLaunchTokenAtom),
  provider: get(externalLaunchProviderAtom),
  launchContext: get(externalLaunchContextAtom),
}));

/**
 * Writes the complete external launch auth state. REPLACES rather than merges:
 * a second call leaves no residue from the first.
 */
export function setExternalLaunchDetails(payload: SetExternalLaunchDetailsPayload): void {
  const store = getDefaultStore();
  store.set(externalLaunchTokenAtom, payload.token);
  store.set(externalLaunchProviderAtom, payload.provider);
  store.set(externalLaunchContextAtom, payload.launchContext);
}

/** Updates only the bearer token and provider, preserving launch context. */
export function setExternalLaunchToken(payload: SetExternalLaunchTokenPayload): void {
  const store = getDefaultStore();
  store.set(externalLaunchTokenAtom, payload.token);
  store.set(externalLaunchProviderAtom, payload.provider);
}

/** Every field back to `null`, unconditionally. */
export function clearExternalLaunchAuth(): void {
  const store = getDefaultStore();
  store.set(externalLaunchTokenAtom, null);
  store.set(externalLaunchProviderAtom, null);
  store.set(externalLaunchContextAtom, null);
}

export function setExternalLaunchContext(context: ExternalLaunchContext | null): void {
  getDefaultStore().set(externalLaunchContextAtom, context);
}

/**
 * The token the axios request interceptors gate on. Not a hook: the
 * interceptors and `getAccessToken`'s refresh path run outside React and before
 * anything mounts, so they read through the atom store directly.
 */
export function getExternalLaunchToken(): string | null {
  return getDefaultStore().get(externalLaunchTokenAtom);
}

/**
 * The provider a force-refresh preserves when `auth.authProvider` is absent.
 * Same reason as the token getter: the caller is `applySuccessOutcome`, which
 * is a plain function on the refresh path.
 */
export function getExternalLaunchProvider(): AuthProvider | null {
  return getDefaultStore().get(externalLaunchProviderAtom);
}
